import type { Router } from 'vue-router';
import { getAuth0Client, getAuth0RedirectUri } from '@/auth0';
import store from '@/store';
import { getAuthRedirectAppState } from '@/utils/authRedirectTarget';
import { getAuthSessionManager } from '@/utils/authSessionManager';
import { notifyError } from '@/utils/notifier.js';

const AUTH_REDIRECT_FAILURE_MESSAGE = 'We could not redirect you to login. Please try again.';

async function redirectToAuth0Login(target: string): Promise<void> {
  const auth0 = getAuth0Client();

  await auth0.loginWithRedirect({
    appState: getAuthRedirectAppState(target),
    authorizationParams: {
      redirect_uri: getAuth0RedirectUri(),
    },
  });
}

export function setupRouterGuards(router: Router) {
  router.beforeEach(async (to, from, next) => {
    // Set page title
    document.title = to.meta.title || 'Welcome!';

    // Authentication guard
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      let wasSharedLogoutMarkerApplied = false;

      try {
        wasSharedLogoutMarkerApplied = await getAuthSessionManager().applySharedLogoutMarkerIfNeeded();
      } catch {
        next(false);
        return;
      }

      if (wasSharedLogoutMarkerApplied) {
        try {
          await redirectToAuth0Login(to.fullPath);
        } catch {
          notifyError(false, AUTH_REDIRECT_FAILURE_MESSAGE, "negative", "report_problem");
        }

        next(false);
        return;
      }

      if (!store.state.isAuthenticated) {
        try {
          await redirectToAuth0Login(to.fullPath);
        } catch {
          notifyError(false, AUTH_REDIRECT_FAILURE_MESSAGE, "negative", "report_problem");
        }

        next(false);
        return;
      }
    }

    // Visitor-only guard (login pages)
    if (to.matched.some((record) => record.meta.requiresVisitor)) {
      if (to.name === 'AuthCallback') {
        next();
        return;
      }

      if (store.state.isAuthenticated && from.fullPath) {
        next({ path: '/home' });
        return;
      }
    }

    next();
  });
}
